import React, {useEffect, useState, useCallback, useRef} from "react";
import MypageLayout from "./MypageLayout";
import AttendanceByDate from "./AttendanceByDate";
import RoleDispatch from "./roleDispatch";
import Markdown from "./Markdown";
import ReportEditor from "./ReportEditor";
import MonthPicker from "./MonthPicker";
import { WD, pad2 } from "./dateUtil";
import { jsonHeaders } from "./csrf";
import { emitToast } from "./Toast";
import ErrorBar from "./ErrorBar";
import EmptyState from "./EmptyState";
import Loader from "./Loader";
import useEscapeKey from "./useEscapeKey";

// 出退勤履歴ページ。役割で出し分ける:
//  ・社員: 自分の出退勤履歴（本コンポーネント OwnAttendanceView）
//  ・経営者(is_executive): 全社員の勤怠管理（日付別・AttendanceByDate）
// 打刻は LINE(LIFF) と PC（ホームの打刻カード）から行える。ここは履歴の閲覧＋日報の再編集。
const API_URL = process.env.REACT_APP_API_URL || "";

const WORK_MODE = { office: "事務所", remote: "在宅" };

// API・画面内部のYYYYMMと、月ピッカーのYYYY-MMを境界で相互変換する。
const ymDash = (ym) => `${ym.slice(0, 4)}-${ym.slice(4, 6)}`;
const ymPlain = (ym) => ym.replace("-", "");

function currentYm() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

// テーブルの出勤/退勤セル用。曜日を括弧付きで含める（例: 07/05(土) 09:05）。
function formatDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}(${WD[d.getDay()]}) ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// モバイルのリスト表示用：日付(MM/DD)・曜日・時刻(HH:MM)を個別に取り出す。
function dateOnly(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;
}
function weekdayJa(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : WD[d.getDay()];
}
function timeOnly(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function OwnAttendanceView() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // 日報モーダルに表示中のレコードID（null=閉じている）。行クリックで開く。
  const [selectedId, setSelectedId] = useState(null);
  // 日報の再編集（退勤後も本人が書き直せる）。編集中のレコードID（null=閉じている）と送信中フラグ。
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  // 月フィルタ（縦伸び対策）。選択中の月だけをサーバから取る。
  // 月の一覧はサーバの months（記録のある月・件数上限に依らず完全）をそのまま使う。
  // 全件取得してクライアント側で月を組み立てると、上限に達した時点で古い月が静かに
  // 消える（年約250件＝4年目に既定1000へ到達）。日報本文込みの全件転送も避ける。
  const [monthsAvailable, setMonthsAvailable] = useState([]);
  const [selectedYm, setSelectedYm] = useState(null);
  // 右の日報パネル（経営者側と同じ常設パネル）。モバイルは選択時にここへスクロールする。
  const detailRef = useRef(null);
  const monthRecords = records;
  // 月を切り替えたら日報の選択を解除（別の月の日報が残るのを防ぐ）。
  useEffect(() => { setSelectedId(null); }, [selectedYm]);

  // Esc で日報モーダルを閉じる。
  useEscapeKey(() => setSelectedId(null), selectedId != null);

  // 月の一覧を取得し、選択月を最新月に初期化する（初回のみ）。
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/employee/attendances/?limit=1`, { credentials: "include" });
        if (res.status === 401) { if (alive) { setError("ログインが必要です。"); setLoading(false); } return; }
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (!alive) return;
        const ms = data.months || [];
        setMonthsAvailable(ms);
        if (ms.length === 0) { setLoading(false); return; }  // 記録なし＝以降の取得は不要
        setSelectedYm(ms[ms.length - 1]);
      } catch (e) {
        if (alive) { setError("履歴の取得に失敗しました。"); setLoading(false); }
      }
    })();
    return () => { alive = false; };
  }, []);

  // 選択月の記録を取得（月を切り替えるたび）。
  const fetchAttendances = useCallback(async () => {
    if (!selectedYm) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/employee/attendances/?ym=${selectedYm}`, { credentials: "include" });
      if (res.status === 401) { setError("ログインが必要です。"); return; }
      if (!res.ok) throw new Error("取得に失敗しました");
      const data = await res.json();
      setRecords(data.results || []);
      if (data.months) setMonthsAvailable(data.months);  // 申請等で月が増えた場合に追従
    } catch (e) {
      setError("履歴の取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, [selectedYm]);

  useEffect(() => { fetchAttendances(); }, [fetchAttendances]);

  // テーブル / リストで共通利用する描画ヘルパー。
  // 勤務形態(事務所/在宅)。バッジにせず、普通のテキストで表示する。
  const modeText = (r) =>
    r.work_mode
      ? <span className="mp-att-mode">{WORK_MODE[r.work_mode] || r.work_mode}</span>
      : <span className="mp-muted">-</span>;
  const statusBadge = (r) =>
    r.status === "open"
      ? <span className="mp-bd mp-work">勤務中</span>
      : <span className="mp-bd mp-done">退勤済</span>;
  // 日報がある行をクリック＝右パネル（常設）に表示。モバイルは右カラムが下に来るのでそこへスクロール。
  const openReport = (r) => {
    if (!r.daily_report) return;
    setSelectedId(r.id);
    window.setTimeout(() => {
      if (detailRef.current && window.innerWidth <= 860) detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };
  const viewCell = (r) =>
    r.daily_report
      ? <span className="mp-att-view">見る<span className="mp-att-chev" aria-hidden="true">›</span></span>
      : <span className="mp-muted">-</span>;

  const selected = records.find((r) => r.id === selectedId) || null;
  const editing = records.find((r) => r.id === editingId) || null;

  // 日報を保存（再編集）。本人のセッションで自分の記録の daily_report のみ更新する。
  const saveReport = async (report) => {
    if (saving || editingId == null) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/employee/self/report/`, {
        method: "POST",
        credentials: "include",
        headers: jsonHeaders(),
        body: JSON.stringify({ id: editingId, daily_report: report }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || "日報の保存に失敗しました。");
      }
      setEditingId(null);
      emitToast("日報を保存しました");
      fetchAttendances();
    } catch (e) {
      setError(e.message || "日報の保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  return (
    <MypageLayout active="attendance" title="勤怠履歴">
      {error && <ErrorBar message={error} onClose={() => setError("")} />}

      {loading ? (
        <Loader />
      ) : records.length === 0 ? (
        <EmptyState>履歴がありません</EmptyState>
      ) : (
        <>
          {selectedYm && (
            <MonthPicker
              value={ymDash(selectedYm)}
              months={monthsAvailable.map(ymDash)}
              currentYm={currentYm()}
              onChange={(ym) => setSelectedYm(ymPlain(ym))}
            />
          )}

          {/* 経営者側と同じ2カラム。左＝履歴（表/リスト）、右＝日報パネル（常設・オーバーレイ廃止）。 */}
          <div className="mp-att-split">
            <div className="mp-att-tablecol">
              {/* デスクトップ：テーブル表示。日報のある行はクリックで右パネルに表示。 */}
              <div className="mp-tablewrap mp-only-desktop">
                <table className="mp-table">
                  <thead>
                    <tr>
                      <th>出勤</th><th>退勤</th><th>勤務形態</th><th>状態</th><th>日報</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthRecords.map((r) => (
                      <tr
                        key={r.id}
                        className={`${r.daily_report ? "mp-att-clickable" : ""}${selectedId === r.id ? " mp-att-sel" : ""}`}
                        onClick={() => openReport(r)}
                      >
                        <td>{formatDateTime(r.clock_in)}</td>
                        <td>{formatDateTime(r.clock_out)}</td>
                        <td>{WORK_MODE[r.work_mode] || r.work_mode}</td>
                        <td>{statusBadge(r)}</td>
                        <td>{viewCell(r)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* モバイル：リスト表示。日報のあるカードはタップで右パネル（下に表示）へ。 */}
              <div className="mp-att-list mp-only-mobile">
                {monthRecords.map((r) => (
                  <div
                    className={`mp-att-item${r.daily_report ? " mp-att-clickable" : ""}${selectedId === r.id ? " mp-att-sel" : ""}`}
                    key={r.id}
                    onClick={() => openReport(r)}
                  >
                    {/* 左＝日付/勤務形態＋時刻の2段。右＝日報あり導線（›）。 */}
                    <div className="mp-att-main">
                      <div className="mp-att-top">
                        <div className="mp-att-left">
                          <div className="mp-att-date">{dateOnly(r.clock_in)}<span>{weekdayJa(r.clock_in)}</span></div>
                          {modeText(r)}
                        </div>
                      </div>
                      {/* 勤務中は「退勤 -」の代わりに勤務中バッジ。退勤済は退勤時刻を表示。 */}
                      <div className="mp-att-times">
                        {r.status === "open" ? (
                          <span>出勤 <b>{timeOnly(r.clock_in)}</b> ・ <span className="mp-att-now">勤務中</span></span>
                        ) : (
                          <span>出勤 <b>{timeOnly(r.clock_in)}</b> ・ 退勤 <b>{timeOnly(r.clock_out)}</b></span>
                        )}
                      </div>
                    </div>
                    {r.daily_report && <span className="mp-att-view">日報<span className="mp-att-chev" aria-hidden="true">›</span></span>}
                  </div>
                ))}
              </div>
            </div>

            {/* 右カラム＝日報パネル（常設）。経営者側の申請/日報パネルと同じ見た目。
                日報のある行を選ぶと表示、未選択時はプレースホルダ。モバイルは選択時のみ下に出る。 */}
            <aside className={`mp-att-detail${selected && selected.daily_report ? " mp-open" : ""}`} ref={detailRef}>
              {selected && selected.daily_report ? (
                <>
                  <div className="mp-att-detail-head">
                    <div>
                      <div className="mp-eyebrow">日報</div>
                      <h3>{dateOnly(selected.clock_in)}（{weekdayJa(selected.clock_in)}）</h3>
                    </div>
                    {/* 退勤後も本人が書き直せる（ユーザー方針）。編集は共通の日報エディタを開く。 */}
                    <button className="mp-btn mp-ghost mp-sm" onClick={() => setEditingId(selected.id)}>編集</button>
                  </div>
                  <div className="mp-att-detail-body">
                    <Markdown text={selected.daily_report} />
                  </div>
                </>
              ) : (
                <div className="mp-att-detail-empty">日報のある行を選ぶと、ここに表示されます。</div>
              )}
            </aside>
          </div>

          {/* 日報の再編集（退勤後）。開くたびに新規インスタンスにするため key を editingId に紐づける。 */}
          <ReportEditor
            key={editingId ?? "closed"}
            open={editingId != null}
            title="日報を編集"
            initialText={editing?.daily_report || ""}
            submitLabel="保存する"
            busy={saving}
            onCancel={() => { if (!saving) setEditingId(null); }}
            onSubmit={saveReport}
          />
        </>
      )}
    </MypageLayout>
  );
}

// 役割で出し分けるディスパッチャ。判定は共通の RoleDispatch に委譲する
// （ここに同じ実装のコピーを持っていたため、/me 失敗時のフォールバック不具合が
//  2箇所に存在していた。判定ロジックは1箇所に集約する）。
function AttendancePage() {
  return (
    <RoleDispatch
      active="attendance"
      title="勤怠履歴"
      Employee={OwnAttendanceView}
      Executive={AttendanceByDate}
    />
  );
}

export default AttendancePage;
